/**
 * useGameTimer — Self-contained game timer (refs only, NO React state).
 *
 * PERF: the previous implementation called setTime() inside a rAF loop,
 * re-rendering the ENTIRE GamePlay tree ~60x/s. The timer now lives purely
 * in refs; the display (TimerPill) subscribes via `getElapsed()` and runs
 * its own local rAF, so nothing else in the tree re-renders per frame.
 *
 * API: start / stop / pause / resume / reset / addPenalty / getElapsed.
 * getElapsed() returns the FROZEN elapsed value while stopped/paused.
 */
import { useCallback, useRef } from "react";

export function useGameTimer() {
  const startRef = useRef(null);
  const runningRef = useRef(false);
  const pausedElapsedRef = useRef(0);

  const getElapsed = useCallback(() => {
    if (runningRef.current && startRef.current != null) {
      return (performance.now() - startRef.current) / 1000;
    }
    return pausedElapsedRef.current || 0;
  }, []);

  const start = useCallback(() => {
    if (runningRef.current) return;
    startRef.current = performance.now();
    pausedElapsedRef.current = 0;
    runningRef.current = true;
  }, []);

  const stop = useCallback(() => {
    if (runningRef.current) {
      pausedElapsedRef.current = (performance.now() - startRef.current) / 1000;
      runningRef.current = false;
    }
  }, []);

  // pause === stop (freeze elapsed); kept as a named alias for call-site clarity.
  const pause = stop;

  const resume = useCallback(() => {
    if (runningRef.current) return;
    startRef.current = performance.now() - pausedElapsedRef.current * 1000;
    runningRef.current = true;
  }, []);

  const reset = useCallback(() => {
    pausedElapsedRef.current = 0;
    startRef.current = null;
    runningRef.current = false;
  }, []);

  const addPenalty = useCallback((seconds) => {
    const s = Number(seconds || 0);
    if (!s) return;
    if (runningRef.current && startRef.current != null) {
      startRef.current -= s * 1000;
    } else {
      pausedElapsedRef.current = (pausedElapsedRef.current || 0) + s;
    }
  }, []);

  return { start, stop, pause, resume, reset, addPenalty, getElapsed };
}
